import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Params,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { LocalStorageService } from '@core/services/local-storage.service';
import { REQUEST_LIST_STATE_KEY } from '@core/constants';
import { RequestListSavedState } from '@core/types';

/**
 * Гард восстановления состояния списка заявок.
 *
 * Если юзер переходит на страницу списка заявок без query-параметров
 * (rqQuery / limit), а в localStorage есть сохранённое состояние
 * для единого списка, гард подставляет недостающие
 * параметры в URL ДО активации компонента. Благодаря этому первый запрос
 * на бекенд сразу уходит с фильтром — без двойного запроса
 * «сначала без фильтра, потом с фильтром».
 *
 * Параметры, уже присутствующие в URL, имеют приоритет над сохранёнными
 * (диплинки работают как раньше). После редиректа все подставленные
 * параметры есть в URL, поэтому гард пропускает навигацию — зацикливание
 * невозможно.
 */
export const RestoreRequestListStateGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const router = inject(Router);
  const saved = inject(LocalStorageService).getItem<RequestListSavedState>(
    REQUEST_LIST_STATE_KEY,
  );

  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) {
    return true;
  }

  const queryParamMap = route.queryParamMap;
  // Подставляем только те параметры, которых нет в URL
  const missingParams: Params = {};

  if (
    !queryParamMap.has('rqQuery') &&
    saved.rqQuery &&
    typeof saved.rqQuery === 'object' &&
    Object.keys(saved.rqQuery).length > 0
  ) {
    missingParams['rqQuery'] = JSON.stringify(saved.rqQuery);
  }
  if (!queryParamMap.has('limit') && Number(saved.limit) > 0) {
    missingParams['limit'] = saved.limit;
  }
  // Всё уже есть в URL — пропускаем (защита от бесконечного редиректа)
  if (Object.keys(missingParams).length === 0) {
    return true;
  }

  // parseUrl сохраняет путь и уже существующие query-параметры (например timestamp)
  const urlTree = router.parseUrl(state.url);

  urlTree.queryParams = { ...urlTree.queryParams, ...missingParams };

  return urlTree;
};
